import {
  HttpClient,
  provideHttpClient,
  withInterceptors
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding
} from '@angular/router';
import { environment } from '@environment/environment';
import {
  HttpCacheInterceptor,
  HttpErrorInterceptor,
  HttpTokenInterceptor
} from '@interceptors/http.interceptor';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import Aura from '@primeng/themes/aura';
import { firstValueFrom } from 'rxjs';
import { appRoutes } from '@routes/app.routes';
import { AuthService } from '@services/auth.service';
import { BootstrapService } from '@services/bootstrap.service';
import { ConfigService } from '@services/config.service';
import { PageTitleStrategy } from '@services/page-title-strategy.service';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import {
  DialogService,
  DynamicDialogModule,
  DynamicDialogRef
} from 'primeng/dynamicdialog';

const config: SocketIoConfig = {
  url: environment.socketUrl,
  options: {
    reconnection: true,
    reconnectionAttempts: 5, // Limit reconnection attempts
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000, // Connection timeout
    autoConnect: false, // Don't auto-connect, let the service handle it
    transports: ['polling', 'websocket'], // Try polling first, then websocket
    forceNew: true, // Force new connection
    upgrade: true, // Allow transport upgrades
    path: '/socket.io'
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    // Load /assets/config/config.json (per-domain runtime values) before the app bootstraps.
    provideAppInitializer(() => inject(ConfigService).load()),
    // Load public tenant branding (/public/bootstrap) before the app bootstraps
    // so the login/sign-up/forgot-password screens paint already branded.
    provideAppInitializer(() => inject(BootstrapService).load()),
    // Access token now lives in memory only (AS-08/AS-09), so a hard page
    // reload always starts logged-out from AuthGuard's point of view. Silently
    // try to mint a fresh access token from the httpOnly refresh_token cookie
    // before the router activates, so a previously logged-in user isn't
    // bounced to /login on every reload. No-op (resolves false) if there's no
    // valid session — never blocks or fails app bootstrap.
    provideAppInitializer(() =>
      firstValueFrom(inject(AuthService).tryRestoreSession())
    ),
    DialogService,
    DynamicDialogRef,
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding()),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: 'none'
        }
      }
    }),
    provideHttpClient(
      withInterceptors([
        HttpTokenInterceptor,
        HttpErrorInterceptor,
        HttpCacheInterceptor
      ])
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        }
      }),
      SocketIoModule.forRoot(config),
      DynamicDialogModule
    ),
    MessageService,
    { provide: 'STORAGE', useFactory: getStorage },
    { provide: TitleStrategy, useClass: PageTitleStrategy }
  ]
};

export function getStorage() {
  return typeof window !== 'undefined'
    ? {
        localStorage: window.localStorage,
        sessionStorage: window.sessionStorage
      }
    : {
        localStorage: null,
        sessionStorage: null
      };
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}
